import {
    cloneDeep,
    hasIn,
    isEmpty,
    isPlainObject,
    merge,
    omit,
} from 'lodash-es'
import type { AppStateX } from '../../types/mfc'

type DiffOptions<K extends keyof AppStateX> = {
    isBranchRemoved?: boolean
    modifications?: AppStateX | AppStateX[K]
    parentKey?: string
    removals?: AppStateX | AppStateX[K]
    original?: AppStateX | AppStateX[K]
    final?: boolean
    debug?: boolean
}

class StateManagerError extends Error {
    code?: string
    keys?: string
}

export const getDiff = <K extends keyof AppStateX>(
    options: DiffOptions<K>,
    level = 0,
) => {
    if (!options) {
        console.error('[ state manager ] Options are required!')
        return
    }

    const {
        isBranchRemoved,
        modifications,
        parentKey,
        removals,
        original,
        final,
        debug,
    } = options

    // console.log('[ state manager ]', JSON.stringify(options, null, 4))

    const isModificationAnObject = isPlainObject(modifications)
    const isRemovalsAnObject = isPlainObject(removals)
    const isOriginalAnObject = isPlainObject(original)

    if (typeof level !== 'number') {
        console.error(
            '[ state manager ] Please do not supply a custom "level" parameter.',
            level,
        )
        return null
    }

    if (level === 0) {
        if (!isOriginalAnObject) {
            console.trace(
                '[ state manager ] Initial "original" state must be an object.',
                options,
            )
            return null
        }

        if (modifications && !isModificationAnObject) {
            console.error(
                '[ state manager ] Initial "modifications" must be an object.',
            )
            return null
        }

        if (modifications && !isModificationAnObject) {
            console.error(
                '[ state manager ] Initial "modifications" must be an object.',
            )
            return null
        }

        if (removals && !isRemovalsAnObject) {
            console.error(
                '[ state manager ] Initial "removals" state must be an object.',
            )
            return null
        }

        if (isEmpty(modifications) && isEmpty(removals)) {
            console.error(
                '[ state manager ] Must provide a modification or removal.',
            )
            return null
        }
    }

    const modificationsObject: AppStateX | AppStateX[K] = isModificationAnObject
        ? modifications
        : ({} as AppStateX | AppStateX[K])
    const removalsObject = isRemovalsAnObject
        ? removals
        : ({} as AppStateX | AppStateX[K])
    const originalObject = isOriginalAnObject
        ? original
        : ({} as AppStateX | AppStateX[K])

    const allKeys = Object.keys({
        ...modificationsObject,
        ...removalsObject,
        ...originalObject,
    })

    const state: Omit<NewState, 'stack'> = {
        impactedState: {} as AppStateX,
        newState: {} as AppStateX,
        rootKeyChanges: [],
        hasChanged: false,
        json: [],
    }

    allKeys.forEach((key) => {
        const isKeyInModifications = key in modificationsObject
        const isKeyInRemovals = key in removalsObject
        const isKeyInOriginal = key in originalObject

        const modificationsValue = modificationsObject[key]
        const removalsValue = removalsObject[key]
        const originalValue = originalObject[key]

        const isModificationValueAnObject = isPlainObject(modificationsValue)
        const isRemovalsValueAnObject = isPlainObject(removalsValue)
        const isOriginalValueAnObject = isPlainObject(originalValue)

        if (debug) {
            console.log('\n\nlevel:', level, ', key:', key)
            console.log('isBranchRemoved', isBranchRemoved)
            console.log(
                'isKeyInModifications:',
                isKeyInModifications,
                ', modificationsValue:',
                modificationsValue,
                ', isModificationValueAnObject:',
                isModificationValueAnObject,
            )
            console.log(
                'isKeyInRemovals:',
                isKeyInRemovals,
                ', removalsValue:',
                removalsValue,
                ', isRemovalsValueAnObject:',
                isRemovalsValueAnObject,
            )
            console.log(
                'isKeyInOriginal:',
                isKeyInOriginal,
                ', originalValue:',
                originalValue,
                ', isOriginalValueAnObject:',
                isOriginalValueAnObject,
            )
        }

        if (
            isKeyInModifications &&
            isKeyInRemovals &&
            (!isModificationValueAnObject || !isRemovalsValueAnObject)
        ) {
            const error: StateManagerError = new Error(
                '[ state manager ] Cannot modify and remove a key at the same time.',
            )

            error.code = 'REMOVAL_MODIFICATION_CONFLICT'
            error.keys = key

            throw error
        }

        if (isKeyInRemovals && !isKeyInOriginal) {
            const error: StateManagerError = new Error(
                '[ state manager ] Cannot remove a key that does not exist in the original.',
            )

            error.code = 'REMOVAL_ORIGINAL_CONFLICT'
            error.keys = key

            throw error
        }

        if (isKeyInModifications && !isModificationValueAnObject) {
            if (originalValue === modificationsValue) {
                state.json.push({
                    value: originalValue,
                    level: level + 1,
                    key,
                })
            } else {
                state.hasChanged = true

                state.json.push({
                    oldValue:
                        isKeyInOriginal && !isOriginalValueAnObject
                            ? originalValue
                            : undefined,
                    insertion: !isKeyInOriginal || isOriginalValueAnObject,
                    newValue: modificationsValue,
                    modification: true,
                    level: level + 1,
                    key,
                })

                state.impactedState[key] = modificationsValue
            }

            state.newState[key] = modificationsValue
        }

        if ((isKeyInRemovals && !isRemovalsValueAnObject) || isBranchRemoved) {
            if (!isOriginalValueAnObject) {
                state.hasChanged = true

                state.json.push({
                    oldValue: originalValue,
                    level: level + 1,
                    removal: true,
                    key,
                })

                state.impactedState[key] = originalValue

                return
            }
        }

        if (
            isKeyInOriginal &&
            !isKeyInModifications &&
            !isKeyInRemovals &&
            !isOriginalValueAnObject &&
            !isBranchRemoved
        ) {
            state.json.push({
                value: originalValue,
                level: level + 1,
                key,
            })

            state.newState[key] = originalValue
        }

        if (
            isModificationValueAnObject ||
            isRemovalsValueAnObject ||
            isOriginalValueAnObject
        ) {
            const subOptions = {
                isBranchRemoved:
                    isBranchRemoved ||
                    (isKeyInModifications && !isModificationValueAnObject) ||
                    (isKeyInRemovals && !isRemovalsValueAnObject),
                modifications: modificationsValue,
                removals: removalsValue,
                original: originalValue,
                parentKey: key,
                debug,
            }

            try {
                const subResult = getDiff(subOptions, level + 1)

                const { impactedState, hasChanged, newState, json } = subResult

                state.hasChanged = state.hasChanged || hasChanged

                state.json.push(...json)

                if (!(key in state.newState)) {
                    if (level === 0 && hasChanged) {
                        state.rootKeyChanges.push(key)
                    }

                    if (hasChanged) {
                        state.impactedState[key] = impactedState
                    }

                    if (isEmpty(newState) && subOptions.isBranchRemoved) {
                        return
                    }

                    state.newState[key] = newState
                }
            } catch (error) {
                error.keys = key + '.' + error.keys
                throw error
            }
        }
    })

    if (level === 0 && !state.hasChanged && !final) {
        return null
    }

    const isEmptyRemovalObject =
        isEmpty(state.newState) && isEmpty(original) && !isBranchRemoved

    state.json.unshift({
        modification: !isOriginalAnObject,
        closing: isEmptyRemovalObject,
        removal: isBranchRemoved,
        key: parentKey,
        opening: true,
        level,
    })

    if (!isEmptyRemovalObject) {
        state.json.push({
            modification: !isOriginalAnObject,
            removal: isBranchRemoved,
            closing: true,
            level,
        })
    }

    const { newState, ...other } = state

    return { ...other, newState: Object.freeze(newState) }
}

export type DiffJSON = {
    modification: boolean
    insertion: boolean
    oldValue: any
    newValue: any
    removal: boolean
    opening: boolean
    closing: boolean
    value: any
    level: number
    dots: boolean
    key: string
}

export const printDiff = (
    json: Array<Partial<DiffJSON>>,
    title = 'UNTITLED',
    stack = '',
) => {
    const buildSpaces = (count?: number, space = '    ') =>
        count
            ? new Array(count)
                  .fill(0)
                  .map(() => space)
                  .join('')
            : ''

    const [, , line2 = '' /* line3 = '' */] = stack.split('\n')
    // const callerFilename = line3.match('\\(webpack-internal:///(.*)\\)')
    const callerFunction = line2.match('at (.*) \\(')

    console.log(
        '\n%c-----------------------------------\n',
        'margin-left:-24px;color:orange;background:orange;border-top-right-radius:8px',
    )
    console.log(`%c${title}`, 'font-size:24px;font-weight:bold;')

    if (callerFunction) {
        console.log(
            '%c' + callerFunction[1] + '()',
            'font-weight:bold;font-style:italic;',
        )
    }

    const greenBackground = 'background:#e6ffe6;'
    const redBackground = 'background:#ffe6e5;'
    const removalStyle = `text-decoration:line-through;${redBackground}`

    json.forEach((instruction) => {
        const {
            modification,
            insertion,
            oldValue,
            newValue,
            removal,
            opening,
            closing,
            value,
            level,
            dots,
            key,
        } = instruction

        const space = buildSpaces(level)

        const resolveValue = (value) => {
            const formatter = ',\n' + space + buildSpaces(key.length + 2, ' ')

            if (Array.isArray(value)) {
                // return `Array( ${value.length} )`
                return (
                    value
                        .map((item) =>
                            isPlainObject(item)
                                ? JSON.stringify(item, null, 4)
                                : item,
                        )
                        .join(formatter) + '\n'
                )
            }

            return value
        }

        if (dots) {
            console.log(`${space}${key}: { ... }\n`)

            return
        }

        if (opening && closing) {
            if (removal) {
                console.log(
                    `%c${space}%c${key}: {}`,
                    redBackground,
                    removalStyle,
                )
                return
            }

            if (modification) {
                console.log(`%c${space}${key}: {}`, greenBackground)
                return
            }

            console.log(`${space}${key}: {}`)

            return
        }

        if (opening) {
            if (!key) {
                console.log(`${space}{`)
                return
            }

            if (removal) {
                console.log(
                    `%c${space}%c${key}: {`,
                    redBackground,
                    removalStyle,
                )
                return
            }

            if (modification) {
                console.log(`%c${space}${key}: {`, greenBackground)
                return
            }

            console.log(`${space}${key}: {`)

            return
        }

        if (closing) {
            if (removal) {
                console.log(`%c${space}%c}`, redBackground, removalStyle)
                return
            }

            if (modification) {
                console.log(`%c${space}}`, greenBackground)
                return
            }

            console.log(`${space}}`)

            return
        }

        if (removal) {
            console.log(
                `%c${space}%c${key}: ${resolveValue(oldValue)}`,
                redBackground,
                removalStyle,
            )
            return
        }

        if (insertion) {
            console.log(
                `%c${space}${key}: ${resolveValue(newValue)}`,
                greenBackground,
            )
            return
        }

        if (modification) {
            console.log(
                `${space}${key}: %c${resolveValue(newValue)} %c${resolveValue(
                    oldValue,
                )}`,
                greenBackground,
                removalStyle,
            )
            return
        }

        console.log(`${space}${key}: ${resolveValue(value)}`)
    })

    // if (callerFilename) {
    //     console.log(
    //         '%cwebpack-internal:///' + callerFilename[1],
    //         'margin-left:-155px;margin-top:8px;',
    //     )
    // }

    // console.log(stack.replace('Error', 'Stack:'))

    console.log(
        '\n%c-----------------------------------\n',
        'margin-left:-24px;color:orange;background:orange;border-bottom-right-radius:8px',
    )
}

type Options = {
    modifications: AppStateX
    removals: AppStateX
    original?: Partial<AppStateX[keyof AppStateX]>
    debug?: boolean
}

type InitAppStateOptions<K extends keyof AppStateX> = {
    modifications: Partial<AppStateX[K]>
    removals: Partial<AppStateX[K]>
    original?: Partial<AppStateX[K]>
    immediately?: boolean
    debug?: boolean
}

export type NewState = {
    rootKeyChanges?: Array<string>
    impactedState: AppStateX
    hasChanged: boolean
    newState: AppStateX
    stack: string
    json: Array<Partial<DiffJSON>>
}

export type FinalState = Omit<NewState, 'stack'>

export type TrackerState = {
    rootKeyChanges?: Partial<Record<keyof AppStateX, boolean>>
    original?: AppStateX[keyof AppStateX]
    impactedState?: Partial<AppStateX>
    timeout?: ReturnType<typeof setTimeout>
    options?: Options
    final?: FinalState
    steps?: Array<
        NewState & { options?: Partial<InitAppStateOptions<keyof AppStateX>> }
    >
}

const defaultOptions = {
    modifications: {},
    removals: {},
}

const defaultTrackerState: TrackerState = {
    options: defaultOptions,
    rootKeyChanges: {},
    original: null,
    timeout: null,
    final: null,
    steps: [],
}

const state = {
    tracker: cloneDeep(defaultTrackerState),
}

const getDiffSafely = (options: Options) => {
    try {
        return getDiff(options)
    } catch (error) {
        console.error(error)
        return error as Error
    }
}

const mergeOptions = (diffOptions: Options, debug = 0): Options => {
    if (debug > 100) {
        console.log(
            'Stuck in loop:',
            JSON.stringify(state.tracker.options, null, 4),
            JSON.stringify(diffOptions, null, 4),
        )
        return
    }

    const mergedOptions = merge({}, state.tracker.options, diffOptions)
    const result = getDiffSafely(mergedOptions)

    if (!(result instanceof Error)) {
        return mergedOptions
    }

    const error = result as StateManagerError

    if (!error.code) {
        console.error('Something went terrably wrong!', result)
        // throw new Error('This is an error that I do not understand why it would be happening here!', result)
        return
    }

    if (error.code === 'REMOVAL_ORIGINAL_CONFLICT') {
        console.warn(
            'Found a case where this is true:',
            error.code,
            JSON.stringify(state.tracker.options, null, 4),
            JSON.stringify(diffOptions, null, 4),
        )
        state.tracker.options.removals = omit(
            state.tracker.options.removals,
            error.keys,
        )
    }

    if (error.code === 'REMOVAL_MODIFICATION_CONFLICT') {
        console.warn(
            'Found a case where this is true:',
            error.code,
            // JSON.stringify(state.tracker.options, null, 4),
            // JSON.stringify(diffOptions, null, 4),
        )
        const isModificationKeyConflict = hasIn(
            state.tracker.options.modifications,
            error.keys,
        )

        const isRemovalKeyConflict = hasIn(
            state.tracker.options.removals,
            error.keys,
        )

        if (isModificationKeyConflict && isRemovalKeyConflict) {
            throw new Error(
                'This is a conflict that I do not understand why it would be happening here!',
            )
        }

        if (isModificationKeyConflict) {
            state.tracker.options.modifications = omit(
                state.tracker.options.modifications,
                error.keys,
            )
        }

        if (isRemovalKeyConflict) {
            state.tracker.options.removals = omit(
                state.tracker.options.removals,
                error.keys,
            )
        }
    }

    return mergeOptions(diffOptions, debug + 1)
}

type CallbackParams = Omit<TrackerState, 'rootKeyChanges'> & {
    rootKeyChanges?: Array<keyof AppStateX>
    impactedState: AppStateX
    newState: AppStateX
}

export const initAppState = (
    callback: (state: CallbackParams) => void,
    appState: AppStateX = Object.freeze({}),
) => {
    function createAppState<K extends keyof AppStateX>(
        key: K,
        initialState?: Partial<AppStateX[K]>,
    ): (options: Partial<InitAppStateOptions<K>>) => void
    /* eslint-disable-next-line no-redeclare */
    function createAppState(): AppStateX
    /* eslint-disable-next-line no-redeclare */
    function createAppState<K extends keyof AppStateX>(
        key?: K,
        initialState: Partial<AppStateX[K]> = {},
    ) {
        if (!key) {
            return appState
        }

        appState = Object.freeze({ ...appState, [key]: initialState })

        return (options: Partial<InitAppStateOptions<K>>) => {
            // console.log(
            //     '[set state] current app state:',
            //     JSON.stringify(appState[key], null, 4),
            // )

            // console.log('[state manager] update:', JSON.stringify(options, null, 4))

            const { modifications, immediately, removals, debug } = options

            const diffOptions: Options = {
                modifications: modifications ? { [key]: modifications } : {},
                removals: removals ? { [key]: removals } : {},
                original: appState,
            }

            const result = getDiffSafely({ ...diffOptions, debug })

            // console.log(
            //     '[state manager] options with original state:',
            //     JSON.stringify(diffOptions),
            //     appState
            // )

            if (result instanceof Error) {
                const error = result as StateManagerError
                console.error(
                    '[state manager]',
                    'State Manager Error',
                    error.message,
                    error.code,
                    JSON.stringify(diffOptions, null, 4),
                )
                return
            }

            if (result === null) {
                // console.warn('[state manager] No changes to state')

                // console.trace(
                //     '[state manager]',
                //     'No changes to state',
                //     JSON.stringify(diffOptions, null, 4),
                //     JSON.stringify(state, null, 4),
                // )

                return
            }

            const { rootKeyChanges, impactedState, newState } = result

            // removing duplicates
            rootKeyChanges.forEach((key) => {
                state.tracker.rootKeyChanges[key] = true
            })

            state.tracker.impactedState = merge(
                {},
                state.tracker.impactedState,
                impactedState,
            )

            if (state.tracker.timeout) {
                clearTimeout(state.tracker.timeout)
            }

            if (process.env.NODE_ENV === 'development') {
                if (debug) {
                    console.warn(
                        '[state manager] Dev only function to support the snapshot of current state, do not let the repetition confuse you!',
                    )
                }

                const mergedOptions = mergeOptions(diffOptions)

                state.tracker.options = mergedOptions

                if (!state.tracker.original) {
                    state.tracker.original = cloneDeep(appState)
                }

                state.tracker.steps.push({
                    ...result,
                    stack: new Error().stack,
                    options,
                })
            }

            appState = newState

            const execute = () => {
                state.tracker.timeout = null

                const {
                    impactedState,
                    original,
                    options: trackerOptions,
                } = state.tracker

                const rootKeyChanges = Object.keys(
                    state.tracker.rootKeyChanges,
                ) as Array<keyof AppStateX>

                if (process.env.NODE_ENV !== 'development') {
                    callback({
                        newState: appState,
                        rootKeyChanges,
                        impactedState,
                    })
                    return
                }

                if (debug) {
                    console.warn(
                        '[state manager] Dev only function to support the snapshot of current state, do not let the repetition confuse you!',
                    )
                }

                state.tracker.final = getDiff({
                    ...trackerOptions,
                    final: true,
                    original,
                })

                callback({
                    ...state.tracker,
                    newState: appState,
                    rootKeyChanges,
                    impactedState,
                })

                state.tracker = cloneDeep(defaultTrackerState)
            }

            if (immediately) {
                execute()
                return
            }

            state.tracker.timeout = setTimeout(execute, 0)
        }
    }
    return createAppState
}
